import React, { useState } from "react";
import styles from "../../../Admin/widget/WidgetAnalytics.module.css";
import {
  FiSearch, FiChevronLeft, FiChevronRight, FiX,
  FiMonitor, FiSmartphone, FiTablet, FiActivity, FiMoreVertical,
} from "react-icons/fi";

const DeviceIcon = ({ device }) => {
  const d = (device || "").toLowerCase();
  if (d === "mobile") return <FiSmartphone className={styles.deviceIcon} />;
  if (d === "tablet") return <FiTablet     className={styles.deviceIcon} />;
  return <FiMonitor className={styles.deviceIcon} />;
};

const SkeletonRow = () => (
  <tr>
    <td><div className={styles.skeleton} style={{ width: "62px",  height: "13px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "110px", height: "13px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "44px",  height: "20px", borderRadius: "20px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "65px",  height: "13px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "30px",  height: "13px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "34px",  height: "13px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "34px",  height: "13px" }} /></td>
    <td><div className={styles.skeleton} style={{ width: "34px",  height: "13px" }} /></td>
    <td>
      <div className={styles.skeleton} style={{ height: "12px", width: "90px", marginBottom: "4px" }} />
      <div className={styles.skeleton} style={{ height: "10px", width: "70px" }} />
    </td>
    <td><div className={styles.skeleton} style={{ width: "72px",  height: "22px", borderRadius: "20px" }} /></td>
    <td>
      <div style={{ display: "flex", gap: "6px" }}>
        <div className={styles.skeleton} style={{ width: "82px", height: "28px", borderRadius: "8px" }} />
        <div className={styles.skeleton} style={{ width: "30px", height: "28px", borderRadius: "8px" }} />
      </div>
    </td>
  </tr>
);

const TelemetryList = ({
  visitors, onFetchJourney, loading,
  page, perPage, totalCount, status, device,
  onPageChange, onPerPageChange, onSearchChange, onStatusChange, onDeviceChange,
}) => {
  const [searchVal,      setSearchVal]      = useState("");
  const [journeyVisitor, setJourneyVisitor] = useState(null);
  const [journeyEvents,  setJourneyEvents]  = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const handleViewJourney = async (v) => {
    setJourneyVisitor(v);
    setJourneyEvents([]);
    setJourneyLoading(true);
    const { events, visitorInfo } = await onFetchJourney(v.id);
    setJourneyEvents(events || []);
    if (visitorInfo) setJourneyVisitor((prev) => ({ ...prev, ...visitorInfo }));
    setJourneyLoading(false);
  };

  const handleSearchChange = (val) => {
    setSearchVal(val);
    onSearchChange(val);
  };

  const totalPages  = Math.ceil(totalCount / perPage) || 1;
  const startRecord = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const endRecord   = Math.min(page * perPage, totalCount);

  const pageNumbers = [];
  const maxVisible  = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
  let endPage   = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);
  for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

  return (
    <>
      <div className={`${styles.sectionCard} mt-4`}>
        <div className={styles.sectionCardHeader}>
          <div>
            <div className={styles.sectionTitle}>Visitors List</div>
            <div className={styles.sectionSubtitle}>Real-time visitor sessions, device distribution and interaction journey logs</div>
          </div>
        </div>

        <div className={styles.telemetryFilters}>
          <div className={styles.searchBox}>
            <FiSearch />
            <input
              placeholder="Search by visitor ID or mobile..."
              value={searchVal}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className={styles.tabGroup}>
            {["all", "anonymous", "identified"].map((t) => (
              <button key={t} className={`${styles.tabBtn} ${status === t ? styles.tabBtnActive : ""}`} onClick={() => onStatusChange(t)}>
                {t.toUpperCase()}
              </button>
            ))}
          </div>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>DEVICE:</span>
            <select className={styles.filterSelect} value={device} onChange={(e) => onDeviceChange(e.target.value)}>
              <option>All</option>
              <option>Mobile</option>
              <option>Desktop</option>
              <option>Tablet</option>
            </select>
          </div>
        </div>

        <div className={styles.tableResponsive}>
          <table className={styles.telemetryTable}>
            <thead>
              <tr>
                <th>VISITOR ID</th>
                <th>MOBILE NUMBER</th>
                <th>CHAT STARTED</th>
                <th>DEVICE</th>
                <th>SESSIONS</th>
                <th>PAGE VIEWS</th>
                <th>WIDGET OPENS</th>
                <th>WHATSAPP CLICKS</th>
                <th>LAST SEEN</th>
                <th>STATUS</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: perPage > 5 ? 5 : perPage }).map((_, i) => <SkeletonRow key={i} />)
              ) : visitors.length === 0 ? (
                <tr><td colSpan={11} className={styles.tableEmptyCell}>No visitors found</td></tr>
              ) : visitors.map((v, i) => (
                <tr key={i}>
                  <td><span className={styles.visitorId}>{v.id}</span></td>
                  <td>
                    {v.mobile !== "–"
                      ? <span className={styles.mobileNum}>{v.mobile}</span>
                      : <span className={styles.mobileNone}>–</span>}
                  </td>
                  <td>
                    {v.chatStarted
                      ? <span className={styles.chatYes}>+ Yes</span>
                      : <span className={styles.chatNo}>- No</span>}
                  </td>
                  <td><span className={styles.deviceCell}><DeviceIcon device={v.device} />{v.device}</span></td>
                  <td>{v.sessions}</td>
                  <td>{v.pageViews}</td>
                  <td><span className={styles.widgetOpenValue}>{v.widgetOpens}</span></td>
                  <td>{v.waClicks}</td>
                  <td>
                    <div className={styles.lastSeenAgo}>{v.lastSeen}</div>
                    <div className={styles.lastSeenDate}>{v.lastSeenDate}</div>
                  </td>
                  <td>
                    <span className={v.status === "Identified" ? styles.badgeIdentified : styles.badgeAnonymous}>
                      + {v.status}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionBtns}>
                      <button className={styles.viewJourneyBtn} onClick={() => handleViewJourney(v)}>View Journey</button>
                      <button className={styles.moreBtn}><FiMoreVertical /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.tableFooter}>
          <span className={styles.tableFooterInfo}>
            {loading
              ? "Loading…"
              : totalCount === 0
                ? "No visitors found"
                : `Showing ${startRecord} to ${endRecord} of ${totalCount} visitors`}
          </span>
          <div className={styles.pagBtns}>
            <button className={styles.pagIconBtn} disabled={loading || page === 1} onClick={() => onPageChange(page - 1)}><FiChevronLeft /></button>
            {pageNumbers.map((n) => (
              <button key={n} className={`${styles.pagBtn} ${page === n ? styles.pagBtnActive : ""}`} disabled={loading} onClick={() => onPageChange(n)}>{n}</button>
            ))}
            <button className={styles.pagIconBtn} disabled={loading || page >= totalPages} onClick={() => onPageChange(page + 1)}><FiChevronRight /></button>
          </div>
        </div>

      </div>

      {journeyVisitor && (
        <div className={styles.modalOverlay} onClick={() => setJourneyVisitor(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalInner}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIconWrap}><FiActivity /></div>
              <div className={styles.modalHeaderBody}>
                <div className={styles.modalTitleRow}>
                  <span className={styles.modalTitle}>Session Journey Logs</span>
                  <span className={journeyVisitor.status === "Identified" ? styles.modalBadgeIdentified : styles.modalBadgeAnon}>
                    {journeyVisitor.status}
                  </span>
                </div>
                <div className={styles.modalSubtitle}>Detailed step-by-step telemetry of <strong>{journeyVisitor.id}</strong></div>
              </div>
              <button className={styles.modalClose} onClick={() => setJourneyVisitor(null)}><FiX /></button>
            </div>

            <hr className={styles.modalDivider} />

            <div className={styles.modalSummary}>
              {[
                { label: "MOBILE NUMBER",   value: journeyVisitor.mobile },
                { label: "CHAT STARTED",    value: journeyVisitor.chatStarted ? "Yes" : "No", color: journeyVisitor.chatStarted ? "#16a34a" : "#ef4444" },
                { label: "DEVICE",          value: journeyVisitor.device },
                { label: "TOTAL SESSIONS",  value: journeyVisitor.sessions },
                { label: "WHATSAPP CLICKS", value: journeyVisitor.waClicks },
              ].map(({ label, value, color }) => (
                <div key={label} className={styles.modalSummaryItem}>
                  <div className={styles.modalSummaryLabel}>{label}</div>
                  <div className={styles.modalSummaryValue} style={color ? { color } : {}}>{value}</div>
                </div>
              ))}
            </div>

            <div className={styles.activityLabel}>ACTIVITY TIMELINE</div>

            {journeyLoading ? (
              <div className={styles.journeyLoading}>Loading timeline…</div>
            ) : journeyEvents.length === 0 ? (
              <div className={styles.journeyEmpty}>No events recorded for this visitor.</div>
            ) : (
              <div className={styles.timeline}>
                {journeyEvents.map((step, i) => (
                  <div key={i} className={styles.timelineItem}>
                    <div className={styles.timelineLeft}>
                      <div className={styles.timelineDot} style={{ borderColor: step.color, background: `${step.color}18`, boxShadow: `0 0 0 3px ${step.color}22` }} />
                      {i < journeyEvents.length - 1 && <div className={styles.timelineLine} />}
                    </div>
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineEvent}>{step.event}</div>
                      <div className={styles.timelinePageRow}>
                        <span className={styles.timelinePageLabel}>Page:</span>
                        <span className={styles.timelinePage}>{step.page}</span>
                      </div>
                    </div>
                    <div className={styles.timelineTime}>🕐 {step.time}</div>
                  </div>
                ))}
              </div>
            )}

            <hr className={styles.modalDivider} />
            <div className={styles.modalFooterRow}>
              <button className={styles.closeJourneysBtn} onClick={() => setJourneyVisitor(null)}>Close Journeys</button>
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TelemetryList;
